import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete, Box, Button, Card, CardContent, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import type { Uzivatel } from '../../api/JsdhNotifikaceService/generated/models/Uzivatel';
import {
    NAZEV_DRUHU, OdpojeniNastaveni, POPIS_DRUHU, ROZSAH_PRODLEVY, ROZSAH_SPICI_HOD, TEXT_BEZ_PRIJEMCU, formatCas,
} from '../../api/JsdhNotifikaceService/service/OdpojeniService';
import { useUlozOdpojeniMutation } from '../../api/JsdhNotifikaceService/service/OdpojeniMutations';

interface Props {
    nastaveni: OdpojeniNastaveni;
    /** Aktivní členové jednotky (nabídka příjemců). */
    clenove: Uzivatel[];
}

const vRozsahu = (text: string, r: { min: number; max: number }) => {
    if (!/^\d+$/.test(text)) {
        return false;
    }
    const n = Number(text);
    return n >= r.min && n <= r.max;
};

/** Stav formuláře z nastavení z BE; prodleva spících je text, null = prázdné pole. */
const zNastaveni = (n: OdpojeniNastaveni) => ({
    zapnuto: n.zapnuto,
    prodleva: String(n.prodlevaMin),
    spici: n.prodlevaSpiciHod == null ? '' : String(n.prodlevaSpiciHod),
    obnoveni: n.oznamitObnoveni,
    prijemci: n.prijemci,
});

/** Karta jednoho druhu (spec oddíl 7): ukládá se po kartách. */
const OdpojeniKarta: React.FC<Props> = ({ nastaveni, clenove }) => {
    const { druh } = nastaveni;
    const uloz = useUlozOdpojeniMutation();
    const [zapnuto, setZapnuto] = useState(() => zNastaveni(nastaveni).zapnuto);
    const [prodleva, setProdleva] = useState(() => zNastaveni(nastaveni).prodleva);
    const [spici, setSpici] = useState(() => zNastaveni(nastaveni).spici);
    const [obnoveni, setObnoveni] = useState(() => zNastaveni(nastaveni).obnoveni);
    const [prijemci, setPrijemci] = useState<number[]>(() => zNastaveni(nastaveni).prijemci);

    useEffect(() => {
        const stav = zNastaveni(nastaveni);
        setZapnuto(stav.zapnuto);
        setProdleva(stav.prodleva);
        setSpici(stav.spici);
        setObnoveni(stav.obnoveni);
        setPrijemci(stav.prijemci);
    }, [nastaveni]);

    const vybrani = useMemo(() => clenove.filter((c) => prijemci.includes(c.id)), [clenove, prijemci]);
    // Příjemci, kteří mezi aktivními členy nejsou: v nabídce chybí, ale uložení je nesmí tiše vyhodit.
    const skryti = useMemo(() => prijemci.filter((id) => !clenove.some((c) => c.id === id)), [clenove, prijemci]);
    const rozsah = ROZSAH_PRODLEVY[druh];
    const prodlevaOk = vRozsahu(prodleva, rozsah);
    const spiciOk = druh !== 'SHELLY' || vRozsahu(spici, ROZSAH_SPICI_HOD);

    const ulozit = () => uloz.mutate({
        druh,
        data: {
            zapnuto,
            prodlevaMin: Number(prodleva),
            prodlevaSpiciHod: druh === 'SHELLY' ? Number(spici) : null,
            oznamitObnoveni: obnoveni,
            prijemci,
        },
    });

    return (
        <Card variant="outlined" data-testid={`odpojeni-karta-${druh}`}>
            <CardContent>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h6" component="h2">{NAZEV_DRUHU[druh]}</Typography>
                        <Typography variant="body2" color="text.secondary">{POPIS_DRUHU[druh]}</Typography>
                    </Box>
                    <FormControlLabel
                        control={<Switch checked={zapnuto} onChange={(e) => setZapnuto(e.target.checked)} />}
                        label="Upozorňovat"
                    />
                    <TextField
                        id={`prodleva-${druh}`}
                        label="Prodleva (min)"
                        type="number"
                        value={prodleva}
                        onChange={(e) => setProdleva(e.target.value)}
                        error={!prodlevaOk}
                        helperText={`Povoleno ${rozsah.min} až ${rozsah.max} minut.`}
                        inputProps={{ min: rozsah.min, max: rozsah.max }}
                    />
                    {druh === 'SHELLY' && (
                        <TextField
                            id="prodleva-spici-SHELLY"
                            label="Prodleva u spících detektorů (h)"
                            type="number"
                            value={spici}
                            onChange={(e) => setSpici(e.target.value)}
                            error={!spiciOk}
                            helperText={`Povoleno ${ROZSAH_SPICI_HOD.min} až ${ROZSAH_SPICI_HOD.max} hodin. Detektory kouře se ozývají jen občas.`}
                            inputProps={{ min: ROZSAH_SPICI_HOD.min, max: ROZSAH_SPICI_HOD.max }}
                        />
                    )}
                    <FormControlLabel
                        control={<Switch checked={obnoveni} onChange={(e) => setObnoveni(e.target.checked)} />}
                        label="Upozornit i na obnovení"
                    />
                    <Autocomplete
                        multiple
                        options={clenove}
                        value={vybrani}
                        getOptionLabel={(u: Uzivatel) => u.jmeno}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        onChange={(_e, val) => setPrijemci([...val.map((u) => u.id), ...skryti])}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Příjemci"
                                placeholder="Hledat člena"
                                helperText={TEXT_BEZ_PRIJEMCU[druh]}
                            />
                        )}
                    />
                    {nastaveni.zmenil && (
                        <Typography variant="caption" color="text.secondary">
                            Naposledy změnil {nastaveni.zmenil}
                            {nastaveni.zmeneno ? `, ${formatCas(nastaveni.zmeneno)}` : ''}
                        </Typography>
                    )}
                    <Box>
                        <Button variant="contained" onClick={ulozit} disabled={!prodlevaOk || !spiciOk || uloz.isPending}>
                            Uložit
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default OdpojeniKarta;
